import { cn, Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { NYL_PILOT_ADVISOR_FEATURE_FLAG, type PilotCapability } from '@shared';
import {
  useActivePilotProgram,
  useIsPilotCapabilityHidden,
  useIsPilotCapabilityReopenedForSelfPlan,
} from '@/shared/hooks/use-active-pilot-program';

export const NYL_UPDATE_INTAKE_FORM_TOOLTIP =
  'This feature is coming soon for the NYL pilot program';

export interface PilotRestrictedProps {
  children: React.ReactNode;
  capability: PilotCapability;
  side?: 'top' | 'right' | 'bottom' | 'left';
  hidden?: boolean;
  className?: string;
  tooltipText?: string;
  restricted?: boolean;
}

export function PilotRestricted({
  children,
  capability,
  side,
  hidden = false,
  className,
  tooltipText,
  restricted = false,
}: PilotRestrictedProps) {
  const program = useActivePilotProgram();
  const isCapabilityRestricted = !!program?.restrictedCapabilities.includes(capability);
  const isCapabilityHidden = useIsPilotCapabilityHidden(capability);

  if (!restricted && !isCapabilityRestricted) {
    return <>{children}</>;
  }

  if (hidden || isCapabilityHidden) {
    return null;
  }

  return (
    <Tooltip disableHoverableContent>
      <TooltipTrigger asChild>
        <div className={cn('cursor-not-allowed', className)}>
          <div
            className="pointer-events-none opacity-50"
            aria-disabled="true"
            onKeyDownCapture={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            onFocusCapture={e => e.stopPropagation()}
            tabIndex={-1}
          >
            {children}
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent
        side={side}
        className="max-w-64"
      >
        {tooltipText ?? program?.restrictedTooltip}
      </TooltipContent>
    </Tooltip>
  );
}

export function PilotHidden({
  children,
  capability,
}: Pick<PilotRestrictedProps, 'children' | 'capability'>) {
  return useIsPilotCapabilityHidden(capability) ? null : <>{children}</>;
}

interface UpdateIntakeFormPilotRestrictedProps extends Pick<
  PilotRestrictedProps,
  'children' | 'side' | 'className'
> {
  /** The client whose form the control edits. The advisor's own self-plan client stays editable. */
  client: Pick<Client, 'advisorId' | 'isAdvisorSelfPlan'> | null | undefined;
}

export function UpdateIntakeFormPilotRestricted({
  children,
  side,
  className,
  client,
}: UpdateIntakeFormPilotRestrictedProps) {
  const program = useActivePilotProgram();
  const isReopenedForSelfPlan = useIsPilotCapabilityReopenedForSelfPlan('intake-form', client);

  if (isReopenedForSelfPlan) {
    return <>{children}</>;
  }

  return (
    <PilotRestricted
      capability="intake-form"
      side={side}
      className={className}
      tooltipText={
        program?.flagKey === NYL_PILOT_ADVISOR_FEATURE_FLAG
          ? NYL_UPDATE_INTAKE_FORM_TOOLTIP
          : undefined
      }
    >
      {children}
    </PilotRestricted>
  );
}
